export type R2TemporaryCredentials = {
  access_key_id: string;
  secret_access_key: string;
  session_token: string;
};

export type R2Transfer = {
  transport: "S3_TEMPORARY_CREDENTIALS";
  provider: "CLOUDFLARE";
  endpoint: string;
  region: string;
  bucket: string;
  object_key: string;
  permission: "object-read-write" | "object-read-only";
  expires_in_seconds: number;
  multipart_supported: boolean;
  recommended_upload_mode: string;
  credentials: R2TemporaryCredentials;
};

export type R2UploadOptions = {
  signal?: AbortSignal;
  onProgress?: (percent: number) => void;
};

const MULTIPART_THRESHOLD = 16 * 1024 * 1024;
const MULTIPART_PART_SIZE = 8 * 1024 * 1024;

function bytesToHex(bytes: Uint8Array) {
  return Array.from(bytes)
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
}

function copyToArrayBuffer(bytes: Uint8Array): ArrayBuffer {
  const copy = new Uint8Array(bytes.byteLength);
  copy.set(bytes);
  return copy.buffer;
}

async function sha256Hex(input: ArrayBuffer | Uint8Array) {
  const source = input instanceof Uint8Array ? copyToArrayBuffer(input) : input;
  return bytesToHex(new Uint8Array(await crypto.subtle.digest("SHA-256", source)));
}

async function hmacSha256(key: Uint8Array, data: string) {
  const cryptoKey = await crypto.subtle.importKey(
    "raw",
    copyToArrayBuffer(key),
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign"],
  );
  return new Uint8Array(
    await crypto.subtle.sign(
      "HMAC",
      cryptoKey,
      copyToArrayBuffer(new TextEncoder().encode(data)),
    ),
  );
}

function awsEncode(value: string) {
  return encodeURIComponent(value).replace(
    /[!'()*]/g,
    (character) =>
      "%" + character.charCodeAt(0).toString(16).toUpperCase(),
  );
}

function canonicalQueryString(query: Record<string, string>) {
  return Object.entries(query)
    .map(([key, value]) => [awsEncode(key), awsEncode(value)] as const)
    .sort(([leftKey, leftValue], [rightKey, rightValue]) =>
      leftKey === rightKey
        ? leftValue.localeCompare(rightValue)
        : leftKey.localeCompare(rightKey),
    )
    .map(([key, value]) => key + "=" + value)
    .join("&");
}

async function requestBodyBytes(body?: Blob | string | Uint8Array) {
  if (!body) return new Uint8Array();
  if (body instanceof Uint8Array) return body;
  if (typeof body === "string") return new TextEncoder().encode(body);
  return new Uint8Array(await body.arrayBuffer());
}

async function signedR2Fetch(
  transfer: R2Transfer,
  method: "GET" | "PUT" | "POST" | "DELETE",
  options: {
    body?: Blob | string | Uint8Array;
    contentType?: string;
    query?: Record<string, string>;
    signal?: AbortSignal;
  } = {},
) {
  if (options.signal?.aborted) {
    throw new DOMException("Upload cancelled.", "AbortError");
  }

  const endpoint = new URL(transfer.endpoint);
  const canonicalUri =
    "/" + awsEncode(transfer.bucket) + "/" +
    transfer.object_key.split("/").map(awsEncode).join("/");
  const canonicalQuery = canonicalQueryString(options.query || {});
  const url =
    endpoint.origin +
    canonicalUri +
    (canonicalQuery ? "?" + canonicalQuery : "");
  const payloadBytes = await requestBodyBytes(options.body);
  const payloadHash = await sha256Hex(payloadBytes);

  const now = new Date();
  const amzDate = now.toISOString()
    .replace(/[:-]/g, "")
    .replace(/\.\d{3}Z$/, "Z");
  const dateStamp = amzDate.slice(0, 8);
  const canonicalHeaders =
    "host:" + endpoint.host + "\n" +
    "x-amz-content-sha256:" + payloadHash + "\n" +
    "x-amz-date:" + amzDate + "\n" +
    "x-amz-security-token:" + transfer.credentials.session_token + "\n";
  const signedHeaders =
    "host;x-amz-content-sha256;x-amz-date;x-amz-security-token";
  const canonicalRequest = [
    method,
    canonicalUri,
    canonicalQuery,
    canonicalHeaders,
    signedHeaders,
    payloadHash,
  ].join("\n");
  const credentialScope = dateStamp + "/auto/s3/aws4_request";
  const stringToSign = [
    "AWS4-HMAC-SHA256",
    amzDate,
    credentialScope,
    await sha256Hex(new TextEncoder().encode(canonicalRequest)),
  ].join("\n");

  const kDate = await hmacSha256(
    new TextEncoder().encode(
      "AWS4" + transfer.credentials.secret_access_key,
    ),
    dateStamp,
  );
  const kRegion = await hmacSha256(kDate, "auto");
  const kService = await hmacSha256(kRegion, "s3");
  const kSigning = await hmacSha256(kService, "aws4_request");
  const signature = bytesToHex(await hmacSha256(kSigning, stringToSign));

  const headers = new Headers({
    Authorization:
      "AWS4-HMAC-SHA256 Credential=" +
      transfer.credentials.access_key_id + "/" + credentialScope +
      ", SignedHeaders=" + signedHeaders +
      ", Signature=" + signature,
    "x-amz-content-sha256": payloadHash,
    "x-amz-date": amzDate,
    "x-amz-security-token": transfer.credentials.session_token,
  });
  if (options.contentType) {
    headers.set("Content-Type", options.contentType);
  }

  return await fetch(url, {
    method,
    headers,
    body:
      method === "PUT" || method === "POST"
        ? options.body instanceof Uint8Array
          ? copyToArrayBuffer(options.body)
          : options.body
        : undefined,
    signal: options.signal,
  });
}

async function retry<T>(
  operation: () => Promise<T>,
  attempts = 3,
  signal?: AbortSignal,
) {
  let lastError: unknown = null;
  for (let attempt = 0; attempt < attempts; attempt += 1) {
    if (signal?.aborted) {
      throw new DOMException("Upload cancelled.", "AbortError");
    }
    try {
      return await operation();
    } catch (error) {
      if (
        signal?.aborted ||
        (error instanceof DOMException && error.name === "AbortError")
      ) {
        throw error;
      }
      lastError = error;
      if (attempt + 1 < attempts) {
        await new Promise((resolve) => setTimeout(resolve, 700 * (attempt + 1)));
      }
    }
  }
  throw lastError instanceof Error ? lastError : new Error(String(lastError));
}

function parseUploadId(xml: string) {
  const document = new DOMParser().parseFromString(xml, "application/xml");
  const uploadId = document.querySelector("UploadId")?.textContent?.trim() || "";
  if (!uploadId) {
    throw new Error("Cloudflare R2 did not return a multipart upload ID.");
  }
  return uploadId;
}

function multipartCompletionXml(parts: Array<{ partNumber: number; etag: string }>) {
  return [
    "<CompleteMultipartUpload>",
    ...parts.map(
      ({ partNumber, etag }) =>
        "<Part><PartNumber>" +
        partNumber +
        "</PartNumber><ETag>" +
        etag.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;") +
        "</ETag></Part>",
    ),
    "</CompleteMultipartUpload>",
  ].join("");
}

async function singlePutUpload(
  transfer: R2Transfer,
  file: File,
  contentType: string,
  options: R2UploadOptions,
) {
  options.onProgress?.(0);
  const response = await retry(async () => {
    const next = await signedR2Fetch(transfer, "PUT", {
      body: file,
      contentType,
      signal: options.signal,
    });
    if (!next.ok) {
      throw new Error("Cloudflare R2 upload failed (" + next.status + ").");
    }
    return next;
  }, 3, options.signal);
  options.onProgress?.(100);
  return response;
}

async function multipartUpload(
  transfer: R2Transfer,
  file: File,
  contentType: string,
  options: R2UploadOptions,
) {
  options.onProgress?.(0);

  const create = await retry(async () => {
    const response = await signedR2Fetch(transfer, "POST", {
      query: { uploads: "" },
      signal: options.signal,
    });
    if (!response.ok) {
      throw new Error(
        "Cloudflare R2 multipart initialization failed (" + response.status + ").",
      );
    }
    return response;
  }, 3, options.signal);

  const uploadId = parseUploadId(await create.text());
  const parts: Array<{ partNumber: number; etag: string }> = [];
  let uploadedBytes = 0;

  try {
    const totalParts = Math.ceil(file.size / MULTIPART_PART_SIZE);

    for (let index = 0; index < totalParts; index += 1) {
      if (options.signal?.aborted) {
        throw new DOMException("Upload cancelled.", "AbortError");
      }

      const start = index * MULTIPART_PART_SIZE;
      const end = Math.min(file.size, start + MULTIPART_PART_SIZE);
      const part = file.slice(start, end);
      const partNumber = index + 1;

      const response = await retry(async () => {
        const next = await signedR2Fetch(transfer, "PUT", {
          body: part,
          contentType,
          query: {
            partNumber: String(partNumber),
            uploadId,
          },
          signal: options.signal,
        });
        if (!next.ok) {
          throw new Error(
            "Cloudflare R2 multipart part " +
            partNumber +
            " failed (" +
            next.status +
            ").",
          );
        }
        return next;
      }, 3, options.signal);

      const etag = response.headers.get("etag");
      if (!etag) {
        throw new Error(
          "Cloudflare R2 did not return an ETag for multipart part " +
          partNumber +
          ".",
        );
      }

      parts.push({ partNumber, etag });
      uploadedBytes += part.size;
      options.onProgress?.(
        Math.min(99, Math.round((uploadedBytes / file.size) * 100)),
      );
    }

    const completionBody = multipartCompletionXml(parts);
    const completed = await retry(async () => {
      const response = await signedR2Fetch(transfer, "POST", {
        body: completionBody,
        contentType: "application/xml",
        query: { uploadId },
        signal: options.signal,
      });
      if (!response.ok) {
        throw new Error(
          "Cloudflare R2 multipart completion failed (" + response.status + ").",
        );
      }
      return response;
    }, 3, options.signal);

    options.onProgress?.(100);
    return completed;
  } catch (error) {
    try {
      await signedR2Fetch(transfer, "DELETE", {
        query: { uploadId },
      });
    } catch {
      // Best-effort multipart cleanup; preserve the original failure.
    }
    throw error;
  }
}

export async function uploadFileToR2(
  transfer: R2Transfer,
  file: File,
  contentType: string,
  options: R2UploadOptions = {},
) {
  if (
    transfer.multipart_supported &&
    file.size > MULTIPART_THRESHOLD
  ) {
    return await multipartUpload(transfer, file, contentType, options);
  }
  return await singlePutUpload(transfer, file, contentType, options);
}

export async function downloadFileFromR2(transfer: R2Transfer) {
  return await retry(async () => {
    const response = await signedR2Fetch(transfer, "GET");
    if (!response.ok) {
      throw new Error("Cloudflare R2 download failed (" + response.status + ").");
    }
    return await response.blob();
  });
}
